import type {
  SuggestionItem,
  TrendingConfig,
  TrendingGroup,
} from './HeaderSearch.types';

export const TRENDING_MIN_LENGTH = 2;
export const TRENDING_MAX_LENGTH = 50;
export const QS_TOP_LIMIT = 5;
export const UA_TOP_LIMIT = 3;
export const UA_TRENDING_LIMIT = 2;

export type TrendingFilterOptions = {
  /** Queries already on screen (recents, or the top group): never repeated. */
  exclude?: string[];
  /** Terms that drop any query containing them, case-insensitively. */
  denylist?: string[];
  limit: number;
};

const normalize = (query: string) => query.trim().replace(/\s+/g, ' ');

// No locale argument: a shopper's locale says nothing about the script of the
// query, and CJK has no case to fold anyway.
export const comparableQuery = (query: string) =>
  normalize(query).toLocaleLowerCase();

/**
 * The junk filter both trending arms share. Popular-query data is
 * user-generated, so it is bounded in length, folded for case and spacing,
 * kept apart from what the dropdown already lists, and screened against a
 * denylist the content team can change without a deploy.
 */
export const filterTrendingQueries = (
  queries: string[],
  { exclude = [], denylist = [], limit }: TrendingFilterOptions
): string[] => {
  const seen = new Set(exclude.map(comparableQuery));
  const blocked = denylist.map(comparableQuery).filter(Boolean);
  const result: string[] = [];

  for (const query of queries) {
    if (result.length >= limit) {
      break;
    }
    const text = normalize(query);
    const key = text.toLocaleLowerCase();
    if (
      text.length < TRENDING_MIN_LENGTH ||
      text.length > TRENDING_MAX_LENGTH ||
      seen.has(key) ||
      blocked.some((term) => key.includes(term))
    ) {
      continue;
    }
    seen.add(key);
    result.push(text);
  }

  return result;
};

/**
 * Reads `{ denylist: string[] }` from an Amplitude variant payload. The payload
 * is hand-edited JSON, so any other shape is an empty list and stray non-string
 * entries are dropped rather than trusted.
 */
export const denylistFromPayload = (payload: unknown): string[] => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const { denylist } = payload as { denylist?: unknown };
  if (!Array.isArray(denylist)) {
    return [];
  }
  return denylist.filter((term): term is string => typeof term === 'string');
};

export type TrendingInputs = {
  /** The box's current Coveo suggestions: the QS arm's empty-query answer. */
  suggestions: string[];
  isLoadingSuggestions: boolean;
  recents: string[];
};

const trendingItem =
  (group: TrendingGroup) =>
  (rawValue: string): SuggestionItem => ({ type: 'trending', group, rawValue });

/**
 * The trending items an empty box lists below its recents. In the QS arm they
 * are the controller's suggestions, held back while a request is in flight: a
 * typed query's answer can land after the box is cleared, and it must not
 * appear under the Top label. The UA arm ignores the controller entirely, so
 * the two sources never mix.
 */
export const buildTrendingItems = (
  { source, top = [], trending = [], denylist }: TrendingConfig,
  { suggestions, isLoadingSuggestions, recents }: TrendingInputs
): SuggestionItem[] => {
  if (source === 'qs') {
    if (isLoadingSuggestions) {
      return [];
    }
    return filterTrendingQueries(suggestions, {
      exclude: recents,
      denylist,
      limit: QS_TOP_LIMIT,
    }).map(trendingItem('top'));
  }

  const topQueries = filterTrendingQueries(top, {
    exclude: recents,
    denylist,
    limit: UA_TOP_LIMIT,
  });
  const risingQueries = filterTrendingQueries(trending, {
    exclude: [...recents, ...topQueries],
    denylist,
    limit: UA_TRENDING_LIMIT,
  });
  return [
    ...topQueries.map(trendingItem('top')),
    ...risingQueries.map(trendingItem('trending')),
  ];
};

export type SuggestionRun = {
  group?: TrendingGroup;
  entries: { item: SuggestionItem; index: number }[];
};

/**
 * Splits the flat option list into contiguous runs sharing a group, so each
 * trending group can render under its heading while every option keeps its
 * flat index — the one activeIndex and aria-activedescendant use.
 */
export const groupSuggestionRuns = (items: SuggestionItem[]): SuggestionRun[] =>
  items.reduce<SuggestionRun[]>((runs, item, index) => {
    const last = runs[runs.length - 1];
    if (last && last.group === item.group) {
      last.entries.push({ item, index });
    } else {
      runs.push({ group: item.group, entries: [{ item, index }] });
    }
    return runs;
  }, []);
